// The dashboard's single payload — GET /api/dashboard/brief.
//
// One call, one arrangement. Every string here is a column value, a number counted from columns,
// or a server-composed template (see server/utils/dashboardQueries.ts): the page ARRANGES, it
// never derives. The types below mirror `DashboardBrief` exactly — when the server shape changes
// this file changes with it and `bunx tsc --noEmit` says so.
import { request } from './client.js';
import type { RailCell } from '../../shared/rail.js';

/** Routes are CONSTANT per cell/rule — they never vary with the value; only anchors carry refs. */
export type DashRoute = Record<string, unknown> | null;

export type DashRailKey = 'LIVE' | 'MOVES' | 'CLOCK' | 'BALL' | 'NEW';

/** `RailCell` from src/shared/rail.ts with this rail's key union applied — not a hand-mirror. */
export interface DashRailCell extends RailCell {
  key: DashRailKey;
}

export interface DashMove {
  rule: string;
  weight: number;
  title: string;
  why: string;
  chips: string[];
  route: DashRoute;
  engagement: { id: number; code: string } | null;
  /** Portfolio scope: the client a client-scoped rule fired for, so its route can be resolved. */
  client_id?: number;
  /** Rule `estimate-vs-fact` only — a co-existence check, labelled as derived. */
  derived?: boolean;
  /** The gate this move blocks, asserted only where the engagement stands at that gate. */
  gate: string | null;
}

export type ClockKind = 'milestone' | 'review' | 'commitment';

export interface DashClock {
  key: string;
  kind: ClockKind;
  what: string;
  sub: string;
  ref: string;
  engagement_id: number | null;
  client_id: number | null;
  due_date: string;
  days: number;
  standing: string;
  tone: string;
  route: DashRoute;
}

export type LatestSource = 'run' | 'portal-comment' | 'portal-action' | 'signature';

export interface DashLatest {
  key: string;
  source: LatestSource;
  at: string;
  what: string;
  detail: string;
  engagement_id: number | null;
  engagement_code: string | null;
  quote: string | null;
  quote_src: string[];
  route: DashRoute;
  new: boolean;
}

export interface DashRouteCell {
  key: 'documents' | 'register' | 'agent' | 'proposals' | 'contracting' | 'acceptance';
  count: number;
  qualifier: string;
  mark: 'proposed' | null;
  tone: 'ink' | 'attend' | 'nil';
  screen: string;
}

export interface DashGate {
  gate: string;
  title: string;
  all_met: boolean;
  auto_met: number;
  auto_total: number;
  manual_met: number;
  manual_total: number;
  pips: { label: string; met: boolean; detail: string }[];
}

export interface DashEngagement {
  id: number;
  code: string;
  name: string;
  client_id: number;
  client_name: string;
  commercial_model: string;
  currency: string;
  current_phase: string;
  phase_label: string;
  phase_index: number;
  phase_count: number;
  archived_at: string | null;
  created_at: string;
  last_movement_days: number | null;
  gate: DashGate | null;
  gate_error: string | null;
  gate_skipped: string | null;
  routes: DashRouteCell[];
}

export interface DashboardBrief {
  operator: { email: string; last_seen_at: string | null };
  today: string;
  rail: DashRailCell[];
  queue: DashMove[];
  clocks: DashClock[];
  latest: DashLatest[];
  latest_since: string | null;
  engagements: DashEngagement[];
  /** Non-empty means render degraded per card — never an error page. */
  problems: { source: string; message: string; engagement_code?: string | null }[];
  generated_at: string;
}

/** '04.08.2026' from an ISO date or timestamp. The payload's own date form. */
export const fmtDashDate = (iso: string | null | undefined): string =>
  iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : '—';

/** '05.08 22:40' — the compact stamp the feed and the masthead use. */
export const fmtDashStamp = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (v: number) => String(v).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const dashboardApi = {
  /**
   * `stamp` moves `operator_visits.last_seen_at`. ONLY the initial load may pass it — a poll that
   * stamped would walk the NEW boundary forward every fifteen seconds (F5.4).
   */
  brief: (opts: { stamp?: boolean } = {}) =>
    request<DashboardBrief>(`/dashboard/brief${opts.stamp ? '?stamp=1' : ''}`),
};

export default dashboardApi;
